import { Injectable } from '@angular/core';

import { CatalogAccessLevel, CatalogObject, CatalogObjectPermissions, PermAccessLevel } from '@mirage-projects/cw-models';

import { AuthorizationService } from '../authorization/authorization.service';

/**
 * This service implements the business logic necessary for the application's permissions control.
 */
@Injectable({
    providedIn: 'root',
})
export class PermissionControlService {
    constructor(private authorizationService: AuthorizationService) {}

    /*
        Return true if user can view the object
    */
    canViewByPermissions(obj: CatalogObject): boolean {
        if (this.authorizationService.isUserLoggedAdmin()) {
            return true;
        } else {
            return this.getAccessLevel(obj) !== PermAccessLevel.NONE;
        }
    }

    /*
        Return true if user can write the object
    */
    canWriteByPermissions(obj: CatalogObject): boolean {
        if (this.authorizationService.isUserLoggedAdmin()) {
            return true;
        } else {
            return this.getAccessLevel(obj) === PermAccessLevel.WRITE || this.getAccessLevel(obj) === PermAccessLevel.FULL;
        }
    }

    /*
        Return true if user can delete the object
        if user is admin or connected to site returns always true
    */
    canDeleteByPermissions(obj: CatalogObject): boolean {
        if (this.authorizationService.isUserLoggedAdmin()) {
            return true;
        } else {
            return this.getAccessLevel(obj) === PermAccessLevel.FULL;
        }
    }

    /**
     * Returns the view, update and delete permissions from an object.
     * @param  catalogObject The catalog object to get permissions
     */
    getPermissionsObject(catalogObject: CatalogObject): CatalogObjectPermissions {
        const permissionFlags: CatalogObjectPermissions = {
            viewPermission: this.canViewByPermissions(catalogObject),
            updatePermission: this.canWriteByPermissions(catalogObject),
            deletePermission: this.canDeleteByPermissions(catalogObject),
        };

        return permissionFlags;
    }

    private getAccessLevel(obj: CatalogObject): CatalogAccessLevel {
        if (obj.getAccessLevel) {
            try {
                return obj.getAccessLevel();
            } catch {
                return 'NONE';
            }
        } else {
            return obj.accessLevel;
        }
    }
}
